import React, { useState } from 'react';
import { ClipboardCheck, Plus } from 'lucide-react';
import { cn } from '../../lib/utils';
import { WORKFLOW_CONNECTOR_LINE } from '../../utils/workflowCanvasViewportUtils';
import { buildVerticalConnectorSegment } from '../../utils/workflowConnectorPaths';
import {
  getCardCenterX,
  WORKFLOW_MAIN_COLUMN_WIDTH,
  WORKFLOW_MAIN_CARD_CENTER_X,
} from '../../utils/workflowCardAnchors';

const PLUS_SIZE = 28;
const PLUS_RADIUS = PLUS_SIZE / 2;
const SEGMENT_HEIGHT = WORKFLOW_CONNECTOR_LINE.segmentHeight;
const STROKE = WORKFLOW_CONNECTOR_LINE.color;
const STROKE_WIDTH = WORKFLOW_CONNECTOR_LINE.width;

const PLUS_BUTTON_CLASS = cn(
  'flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border border-slate-300',
  'bg-white text-lg leading-none text-slate-700',
  'shadow-[0_2px_6px_rgba(15,23,42,0.08)] transition-colors',
  'hover:border-emerald-500 hover:bg-emerald-50 hover:text-emerald-600',
  'hover:shadow-[0_6px_14px_rgba(15,23,42,0.10)]',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
);

const PasteTooltip = ({ visible }) => {
  if (!visible) return null;
  return (
    <div
      className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2.5 py-1 text-xs font-medium text-white shadow-lg"
      role="tooltip"
    >
      Paste here
      <div className="absolute left-1/2 top-full -mt-px h-0 w-0 -translate-x-1/2 border-x-4 border-t-4 border-x-transparent border-t-slate-900" />
    </div>
  );
};

const ConnectorSvg = ({
  centerX,
  columnWidth,
  totalHeight,
  readOnly = false,
  showTop = true,
  showBottom = true,
  continuous = false,
  svgTestId,
}) => {
  if (continuous && showTop && showBottom) {
    return (
      <svg
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        width={columnWidth}
        height={totalHeight}
        aria-hidden="true"
        data-workflow-connector-svg
        data-workflow-step-connector-svg="true"
        data-testid={svgTestId}
      >
        <path
          d={buildVerticalConnectorSegment({
            mergeX: centerX,
            fromY: 0,
            toY: totalHeight,
            extendEnd: 2,
          })}
          fill="none"
          stroke={STROKE}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          data-workflow-connector-path="continuous"
          data-workflow-connector-anchor-top={`${centerX},0`}
          data-workflow-connector-anchor-bottom={`${centerX},${totalHeight}`}
          data-testid="workflow-connector-line-continuous"
        />
      </svg>
    );
  }

  const plusCenterY = totalHeight / 2;
  const topEndY = readOnly ? totalHeight / 2 : plusCenterY - PLUS_RADIUS;
  const bottomStartY = readOnly ? totalHeight / 2 : plusCenterY + PLUS_RADIUS;

  return (
    <svg
      className="pointer-events-none absolute left-0 top-0 overflow-visible"
      width={columnWidth}
      height={totalHeight}
      aria-hidden="true"
      data-workflow-connector-svg
      data-workflow-step-connector-svg="true"
      data-testid={svgTestId}
    >
      {showTop ? (
        <path
          d={buildVerticalConnectorSegment({ mergeX: centerX, fromY: 0, toY: topEndY })}
          fill="none"
          stroke={STROKE}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          data-workflow-connector-path="top"
          data-workflow-connector-anchor-top={`${centerX},0`}
          data-testid="workflow-connector-line-top"
        />
      ) : null}
      {showBottom && !readOnly ? (
        <path
          d={buildVerticalConnectorSegment({
            mergeX: centerX,
            fromY: bottomStartY,
            toY: totalHeight,
          })}
          fill="none"
          stroke={STROKE}
          strokeWidth={STROKE_WIDTH}
          strokeLinecap="round"
          strokeLinejoin="round"
          data-workflow-connector-path="bottom"
          data-workflow-connector-anchor-bottom={`${centerX},${totalHeight}`}
          data-testid="workflow-connector-line-bottom"
        />
      ) : null}
    </svg>
  );
};

export const WorkflowStepConnector = ({
  onAdd,
  showPaste = false,
  onPaste,
  pasteValid = true,
  readOnly = false,
  showAddButton = true,
  showTopLine = true,
  showBottomLine = true,
  connectorTestId,
  addTestId,
  pasteTestId,
  insertionPointId,
  columnWidth = WORKFLOW_MAIN_COLUMN_WIDTH,
  centerX: _centerXOverride = null,
}) => {
  const [pasteHovered, setPasteHovered] = useState(false);
  const resolvedColumnWidth = WORKFLOW_MAIN_COLUMN_WIDTH;
  const centerX = WORKFLOW_MAIN_CARD_CENTER_X;
  const hasAddButton = showAddButton && !readOnly;
  const totalHeight = readOnly
    ? SEGMENT_HEIGHT * 2
    : hasAddButton
      ? SEGMENT_HEIGHT * 2 + PLUS_SIZE
      : SEGMENT_HEIGHT;
  const plusCenterY = hasAddButton ? SEGMENT_HEIGHT + PLUS_RADIUS : totalHeight / 2;

  if (readOnly) {
    return (
      <div
        className="relative mx-auto"
        style={{ width: resolvedColumnWidth, height: totalHeight }}
        data-testid={connectorTestId}
        data-workflow-connector
        data-workflow-main-center-x={centerX}
        data-workflow-merge-point={`${centerX},${plusCenterY}`}
        data-workflow-insertion-point={insertionPointId}
      data-workflow-step-connector="true"
      data-workflow-step-connector-center-x={centerX}
      data-workflow-step-connector-plus-center={`${centerX},${plusCenterY}`}
      >
        <ConnectorSvg
          centerX={centerX}
          columnWidth={resolvedColumnWidth}
          totalHeight={totalHeight}
          readOnly
          showTop={showTopLine}
          showBottom={showBottomLine}
        svgTestId="workflow-step-connector-svg"
        />
      </div>
    );
  }

  return (
    <div
      className="relative mx-auto"
      style={{ width: resolvedColumnWidth, height: totalHeight }}
      data-workflow-connector
      data-testid={connectorTestId}
      data-workflow-main-center-x={centerX}
      data-workflow-card-center-x={centerX}
      data-workflow-merge-point={`${centerX},${plusCenterY}`}
      data-workflow-insertion-point={insertionPointId}
      data-workflow-step-connector="true"
      data-workflow-step-connector-center-x={centerX}
      data-workflow-step-connector-plus-center={`${centerX},${plusCenterY}`}
    >
      <ConnectorSvg
        centerX={centerX}
        columnWidth={resolvedColumnWidth}
        totalHeight={totalHeight}
        showTop={showTopLine}
        showBottom={showBottomLine}
        continuous={hasAddButton}
        svgTestId="workflow-step-connector-svg"
      />

      {hasAddButton ? (
        <div
          className="absolute z-[2] flex items-center gap-2"
          style={{
            left: centerX,
            top: plusCenterY,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <button
            type="button"
            onClick={onAdd}
            onPointerDown={(event) => event.stopPropagation()}
            aria-label="Add action"
            className={PLUS_BUTTON_CLASS}
            data-workflow-connector
            data-workflow-add-button
            data-workflow-step-connector-plus="true"
            data-workflow-step-connector-plus-center={`${centerX},${plusCenterY}`}
            data-workflow-editor-ignore-pan
            data-testid={addTestId}
          >
            <Plus size={14} strokeWidth={2.5} />
          </button>

          {showPaste && (
            <div className="relative">
              <PasteTooltip visible={pasteHovered && pasteValid} />
              <button
                type="button"
                onClick={pasteValid ? onPaste : undefined}
                disabled={!pasteValid}
                onPointerDown={(event) => event.stopPropagation()}
                aria-label="Paste copied action here"
                onMouseEnter={() => setPasteHovered(true)}
                onMouseLeave={() => setPasteHovered(false)}
                onFocus={() => setPasteHovered(true)}
                onBlur={() => setPasteHovered(false)}
                className={cn(
                  'paste-target relative flex h-7 w-7 items-center justify-center rounded-full border bg-white text-sm leading-none shadow-[0_2px_6px_rgba(15,23,42,0.08)] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40',
                  pasteValid
                    ? 'cursor-pointer border-emerald-300 text-emerald-600 hover:border-emerald-500 hover:bg-emerald-50 hover:shadow-[0_6px_14px_rgba(15,23,42,0.10)]'
                    : 'cursor-not-allowed border-slate-200 text-slate-300',
                )}
                data-workflow-connector
                data-workflow-editor-ignore-pan
                data-testid={pasteTestId}
              >
                <ClipboardCheck size={14} strokeWidth={2.1} />
              </button>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default WorkflowStepConnector;
